import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { AdminEntityService } from '../../service/entity.service';
import { ModalLayoutModule } from '../../../../global/modal-layout';
import { UnitDeletionConfirmationData } from '../unit-deletion-confirmation/unit-deletion-confirmation.component';

export interface UnitReplacementConfirmationData extends UnitDeletionConfirmationData {
    gameId: string;
    /** Optional override to format the metadata line shown under each unit in search results. */
    getUnitMeta?: (unit: any) => string;
}

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule, IonicModule, ModalLayoutModule],
    selector: 'abs-unit-replacement-confirmation',
    template: `
        <abs-modal-layout title="Delete: {{ data.entityName }}" [showClose]="false">
            <div class="warning">
                <p>
                    This unit is in <strong>{{ data.affectedForcesCount }}</strong> force{{
                        data.affectedForcesCount !== 1 ? 's' : ''
                    }}
                    owned by <strong>{{ data.affectedUsersCount }}</strong> user{{ data.affectedUsersCount !== 1 ? 's' : '' }}.
                </p>
            </div>

            <!-- Mode toggle -->
            <div class="form-row modeToggle">
                <button [class.active]="mode === 'replace'" (click)="mode = 'replace'">Replace silently</button>
                <button [class.active]="mode === 'alert'" (click)="mode = 'alert'">Remove &amp; alert users</button>
            </div>

            <!-- Replace mode -->
            <ng-container *ngIf="mode === 'replace'">
                <p class="modeHint">
                    The unit will be swapped to a new unit and variant in all affected forces. Injuries, training, and custom names are
                    preserved. Users will not be notified.
                </p>

                <!-- Unit search -->
                <div class="form-row" *ngIf="!selectedUnit">
                    <label>Replacement unit:</label>
                    <ion-searchbar
                        [value]="searchTerm"
                        placeholder="Search by name..."
                        (ionInput)="onSearchInput($event)"
                        [debounce]="0"
                    ></ion-searchbar>
                    <div class="searching" *ngIf="isSearching">Searching…</div>
                    <ul class="searchResults" *ngIf="searchResults.length > 0">
                        <li *ngFor="let unit of searchResults" (click)="selectUnit(unit)">
                            <span class="unitName">{{ unit.name?.en || unit.name || unit.key }}</span>
                            <span class="unitMeta">{{ unitMeta(unit) }}</span>
                        </li>
                    </ul>
                    <div class="noResults" *ngIf="hasSearched && !isSearching && searchResults.length === 0">No units found.</div>
                </div>

                <!-- Selected unit -->
                <div class="form-row" *ngIf="selectedUnit">
                    <label>Replacing with:</label>
                    <div class="selectedUnit">
                        <div class="selectedUnitInfo">
                            <span class="unitName">{{ selectedUnit.name?.en || selectedUnit.name || selectedUnit.key }}</span>
                            <span class="unitMeta">{{ unitMeta(selectedUnit) }}</span>
                        </div>
                        <ion-button fill="clear" class="btn-sm" (click)="clearSelection()">Change</ion-button>
                    </div>
                </div>

                <!-- Variant picker -->
                <div class="form-row" *ngIf="selectedUnit">
                    <label>Weapon set:</label>
                    <select [(ngModel)]="selectedVariantId" class="variantSelect">
                        <option *ngFor="let v of variantOptions" [ngValue]="v.id">{{ v.label }}</option>
                    </select>
                    <div class="noVariants" *ngIf="variantOptions.length === 0">No weapon sets found on this unit.</div>
                </div>
            </ng-container>

            <!-- Alert mode -->
            <ng-container *ngIf="mode === 'alert'">
                <p class="modeHint">The unit will be removed from all affected forces. Users will receive the alert message below.</p>
                <div class="form-row">
                    <label>Alert message:</label>
                    <textarea [(ngModel)]="customAlertMessage" rows="4"></textarea>
                </div>
            </ng-container>

            <div class="buttons">
                <ion-button color="medium" (click)="cancel()">Cancel</ion-button>
                <ion-button
                    color="danger"
                    [disabled]="mode === 'replace' && (!selectedUnit || selectedVariantId == null)"
                    (click)="confirm()"
                >
                    {{ mode === 'replace' ? 'Delete &amp; Replace' : 'Delete &amp; Alert' }}
                </ion-button>
            </div>
        </abs-modal-layout>
    `,
    styles: [
        `
            .warning {
                background: #fff3cd;
                border-left: 4px solid #ffc107;
                padding: 10px 14px;
                border-radius: 4px;
                margin-bottom: 16px;
            }
            .warning p {
                margin: 4px 0;
            }
            .form-row {
                margin-bottom: 16px;
            }
            label {
                display: block;
                font-weight: 600;
                margin-bottom: 6px;
            }
            ion-searchbar {
                padding: 0;
                --box-shadow: none;
                --border-radius: 4px;
            }
            .searchResults {
                list-style: none;
                margin: 4px 0 0;
                padding: 0;
                border: 1px solid #ccc;
                border-radius: 4px;
                max-height: 200px;
                overflow-y: auto;
            }
            .searchResults li {
                padding: 8px 14px;
                cursor: pointer;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }
            .searchResults li:hover {
                background: #f0f0f0;
            }
            .unitName {
                font-weight: 500;
            }
            .unitMeta {
                font-size: 0.8em;
                color: #666;
            }
            .selectedUnit {
                display: flex;
                align-items: center;
                gap: 8px;
                background: #e8f5e9;
                border-radius: 4px;
                padding: 8px 12px;
            }
            .selectedUnitInfo {
                flex: 1 1 auto;
                display: flex;
                flex-direction: column;
                gap: 2px;
            }
            .modeToggle {
                display: flex;
                gap: 0;
                border: 1px solid #ccc;
                border-radius: 6px;
                overflow: hidden;
            }
            .modeToggle button {
                flex: 1 1 50%;
                padding: 9px 12px;
                background: #f5f5f5;
                border: none;
                cursor: pointer;
                font-size: 0.9em;
                transition: background 0.15s;
            }
            .modeToggle button.active {
                background: #333;
                color: #fff;
                font-weight: 600;
            }
            .modeHint {
                font-size: 0.85em;
                color: #555;
                margin: 0 0 12px;
            }
            .variantSelect {
                width: 100%;
                padding: 8px;
                border-radius: 4px;
                border: 1px solid #ccc;
            }
            textarea {
                width: 100%;
                box-sizing: border-box;
                padding: 8px;
                border-radius: 4px;
                border: 1px solid #ccc;
            }
            .searching,
            .noResults,
            .noVariants {
                font-style: italic;
                color: #666;
                margin-top: 6px;
                font-size: 0.9em;
            }
            .buttons {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                margin-top: 8px;
            }
        `
    ]
})
export class UnitReplacementConfirmationComponent implements OnInit, OnDestroy {
    @Input() data: UnitReplacementConfirmationData;

    searchTerm = '';
    searchResults: any[] = [];
    isSearching = false;
    hasSearched = false;

    mode: 'replace' | 'alert' = 'replace';
    customAlertMessage = '';

    selectedUnit: any = null;
    variantOptions: Array<{ id: number; label: string }> = [];
    selectedVariantId: number | null = null;

    private searchSubject = new Subject<string>();
    private searchSubscription: Subscription;

    constructor(
        private modalController: ModalController,
        private entityService: AdminEntityService
    ) {}

    ngOnInit() {
        this.customAlertMessage = this.data?.defaultAlertMessage || '';

        this.searchSubscription = this.searchSubject
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe((term) => this.performSearch(term));
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
    }

    onSearchInput(event: any) {
        const term: string = event?.detail?.value ?? '';
        this.searchTerm = term;
        if (!term.trim()) {
            this.searchResults = [];
            this.hasSearched = false;
            return;
        }
        this.searchSubject.next(term.trim());
    }

    private async performSearch(term: string) {
        this.isSearching = true;
        this.hasSearched = false;
        try {
            const result = await firstValueFrom(this.entityService.getEntities(this.data.gameId, 'units', 0, `name.en:${term}`, '', 20));
            this.searchResults = result?.items ?? [];
        } catch {
            this.searchResults = [];
        } finally {
            this.isSearching = false;
            this.hasSearched = true;
        }
    }

    selectUnit(unit: any) {
        this.selectedUnit = unit;
        this.variantOptions = (unit.weaponSets ?? []).map((ws: any) => ({
            id: ws.id,
            label: this.buildWeaponSetLabel(ws)
        }));
        this.selectedVariantId = this.variantOptions[0]?.id ?? null;
        this.searchResults = [];
        this.searchTerm = '';
        this.hasSearched = false;
    }

    clearSelection() {
        this.selectedUnit = null;
        this.variantOptions = [];
        this.selectedVariantId = null;
    }

    confirm() {
        if (this.mode === 'replace') {
            if (!this.selectedUnit || this.selectedVariantId == null) {
                return;
            }
            this.modalController.dismiss(
                {
                    mode: 'replace',
                    replacementEntityId: this.selectedUnit._id,
                    replacementVariantId: this.selectedVariantId
                },
                'confirm'
            );
        } else {
            this.modalController.dismiss(
                {
                    mode: 'alert',
                    customAlertMessage: this.customAlertMessage
                },
                'confirm'
            );
        }
    }

    cancel() {
        this.modalController.dismiss(null, 'cancel');
    }

    private buildWeaponSetLabel(ws: any): string {
        const label: string | undefined = ws.label?.en;
        const weapons: string = (ws.weapons ?? []).join(', ');
        const base = weapons || `Variant ${ws.id}`;
        return label ? `${label}: ${base}` : base;
    }

    unitMeta(unit: any): string {
        if (this.data?.getUnitMeta) {
            return this.data.getUnitMeta(unit);
        }
        // Generic default: factions and legend/companion badges only
        const parts: string[] = [];
        const factions: string[] = unit.factions ?? [];
        if (factions.length > 0) parts.push(factions.join(', '));
        const badges: string[] = [];
        if (unit.legend) badges.push('Legend');
        if (unit.companion) badges.push('Companion');
        if (badges.length > 0) parts.push(badges.join(', '));
        return parts.join(' · ');
    }
}
